import React, { useState, useMemo } from 'react';

function ApprovalQueue({ tickets, onTicketClick, onApprovalComplete }) {
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const sortedTickets = useMemo(() => {
    let sortableItems = [...tickets];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle specific nested or formatted values
        if (sortConfig.key === 'summary') {
          aValue = `${a.equipment_model} ${a.fault_code}`;
          bValue = `${b.equipment_model} ${b.fault_code}`;
        }

        // Standard comparison
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [tickets, sortConfig]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return ' ↕';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return dateStr;
    }
  };

  if (tickets.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: 48,
        color: 'var(--text-muted)',
      }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
        <div style={{ fontSize: 14 }}>No tickets pending approval</div>
      </div>
    );
  }

  return (
    <div className="ticket-list">
      {/* Header row */}
      <div
        className="ticket-row ticket-row--header"
        style={{
          padding: '0 var(--space-md) var(--space-sm)',
          fontSize: 11,
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          fontWeight: 700
        }}
      >
        <span 
          style={{ cursor: 'pointer', userSelect: 'none' }} 
          onClick={() => requestSort('ticket_id')}
        >
          ID{getSortIndicator('ticket_id')}
        </span>
        <span 
          style={{ cursor: 'pointer', userSelect: 'none' }} 
          onClick={() => requestSort('created_at')}
        >
          Date{getSortIndicator('created_at')}
        </span>
        <span 
          style={{ cursor: 'pointer', userSelect: 'none' }} 
          onClick={() => requestSort('summary')}
        >
          Summary{getSortIndicator('summary')}
        </span>
        <span 
          style={{ cursor: 'pointer', userSelect: 'none' }} 
          onClick={() => requestSort('technician_name')}
        >
          Tech{getSortIndicator('technician_name')}
        </span>
        <span 
          style={{ cursor: 'pointer', userSelect: 'none' }} 
          onClick={() => requestSort('triage_category')}
        >
          Category{getSortIndicator('triage_category')}
        </span>
        <span 
          style={{ cursor: 'pointer', userSelect: 'none' }} 
          onClick={() => requestSort('triage_risk_level')}
        >
          Risk{getSortIndicator('triage_risk_level')}
        </span>
      </div>

      {/* Ticket rows */}
      {sortedTickets.map((ticket) => {
        const riskClass = (ticket.triage_risk_level || 'medium').toLowerCase();

        return (
          <div
            key={ticket.ticket_id}
            className="ticket-row ticket-row--approval"
            onClick={() => onTicketClick(ticket.ticket_id)}
          >
            <span className="ticket-row__id">{ticket.ticket_id}</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {formatDate(ticket.created_at)}
            </span>
            <div>
              <div className="ticket-row__summary">
                {ticket.equipment_model} — {ticket.fault_code}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                {ticket.symptoms?.substring(0, 60)}
                {ticket.symptoms?.length > 60 ? '...' : ''}
              </div>
            </div>
            <span className="ticket-row__tech">{ticket.technician_name}</span>
            <span style={{ fontSize: 12 }}>
              {(ticket.triage_category || '').replace('_', ' ')}
            </span>
            <span className={`badge badge--${riskClass}`}>
              {ticket.triage_risk_level || '?'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default ApprovalQueue;
